import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CACHE_STORAGE } from '@tvd/cdk/browser';
import { ICON_BASE_URL } from './providers';

@Injectable({ providedIn: 'root' })
export class MaterialIconRegistry {
  readonly #http = inject(HttpClient);
  readonly #baseUrl = inject(ICON_BASE_URL);
  readonly #caches = inject(CACHE_STORAGE);

  /**
   * Deduplicates concurrent requests for the same icon.
   * Without this, multiple components requesting the same icon simultaneously
   * would each independently hit the Cache API and potentially fire duplicate
   * HTTP requests before the first one resolves and writes to the cache.
   */
  readonly #inflight = new Map<string, Promise<string>>();
  readonly #missingLogged = new Set<string>();

  readonly #placeholder =
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M12 7V13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>';

  getIcon(name: string): Promise<string> {
    const existing = this.#inflight.get(name);
    if (existing) {
      return existing;
    }

    const request = this.#load(name);
    this.#inflight.set(name, request);
    return request;
  }

  async #load(name: string): Promise<string> {
    const normalizedBaseUrl = this.#baseUrl.replace(/\/+$/, '');
    const url = `${normalizedBaseUrl}/${name}.svg`;

    try {
      const cache = await this.#caches.open('zynday-icons');
      const cached = await cache.match(url);
      if (cached) {
        const svg = await cached.text();
        if (typeof svg === 'string' && svg.trim().startsWith('<svg')) {
          return svg;
        }
      }

      const svg = await firstValueFrom(
        this.#http.get(url, { responseType: 'text' }),
      );
      if (typeof svg !== 'string' || !svg.trim().startsWith('<svg')) {
        this.#logMissing(name, `invalid SVG content from ${url}`);
        return this.#placeholder;
      }

      await cache.put(
        url,
        new Response(svg, { headers: { 'Content-Type': 'image/svg+xml' } }),
      );
      return svg;
    } catch {
      this.#logMissing(name, `missing icon at ${url}`);
      return this.#placeholder;
    }
  }

  #logMissing(name: string, reason: string): void {
    if (this.#missingLogged.has(name)) {
      return;
    }

    this.#missingLogged.add(name);
    console.warn(`[icons] Placeholder rendered for "${name}": ${reason}`);
  }
}
